import { type Event } from '@kalos/kalos-rpc';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { type DirtyModelFields } from '../../../../tools/helpers';

const buildInitialDraft = (existingEvent?: Event): Partial<Event> => {
  if (!existingEvent) return {};

  return {
    id: existingEvent.id,
    departmentId: existingEvent.departmentId,
    logJobStatus: existingEvent.logJobStatus,
    dateStarted: existingEvent.dateStarted,
    dateEnded: existingEvent.dateEnded,
    timeStarted: existingEvent.timeStarted,
    timeEnded: existingEvent.timeEnded,
    description: existingEvent.description,
    name: existingEvent.name,
    logTechnicianAssigned: existingEvent.logTechnicianAssigned,
    logNotes: existingEvent.logNotes,
    jobTypeId: existingEvent.jobTypeId,
    jobSubtypeId: existingEvent.jobSubtypeId,
    logPaymentType: existingEvent.logPaymentType,
    amountQuoted: existingEvent.amountQuoted,
    isCallback: existingEvent.isCallback,
    callbackOriginalId: existingEvent.callbackOriginalId,
    diagnosticQuoted: existingEvent.diagnosticQuoted,
    highPriority: existingEvent.highPriority,
    isLmpc: existingEvent.isLmpc,
    isResidential: existingEvent.isResidential,
    logNotification: existingEvent.logNotification,
    logContractNotes: existingEvent.logContractNotes,
    notes: existingEvent.notes,
  };
};

type UseEventDraftOptions = {
  existingEvent?: Event;
  /** Seed the draft from a restored wizard snapshot (new-job persistence). */
  initialDraft?: Partial<Event>;
  initialDirtyFields?: DirtyModelFields<Record<string, unknown>>;
};

export const useEventDraft = ({
  existingEvent,
  initialDraft,
  initialDirtyFields,
}: UseEventDraftOptions) => {
  const [draft, setDraft] = useState<Partial<Event>>(
    () => initialDraft ?? buildInitialDraft(existingEvent),
  );
  const [dirtyFields, setDirtyFields] = useState<DirtyModelFields<Record<string, unknown>>>(
    () => initialDirtyFields ?? {},
  );

  // Rebuild the draft whenever the *edited* event changes. Skip runs where it hasn't
  // changed (the mount run, and StrictMode's dev-only repeat of it): the initializer above
  // already seeded the draft (from a restored snapshot for new jobs, or from existingEvent
  // when editing), and buildInitialDraft(undefined) would otherwise wipe a restored new-job
  // draft on mount.
  const lastEventRef = useRef(existingEvent);
  useEffect(() => {
    if (lastEventRef.current === existingEvent) return;
    lastEventRef.current = existingEvent;
    setDraft(buildInitialDraft(existingEvent));
    setDirtyFields({});
  }, [existingEvent]);

  const updateDraft = useCallback(
    (updatedData: Partial<Event>, nextDirtyFields?: DirtyModelFields<Record<string, unknown>>) => {
      setDraft((prev) => ({ ...prev, ...updatedData }));
      if (nextDirtyFields) {
        setDirtyFields((prev) => ({ ...prev, ...nextDirtyFields }));
      }
    },
    [],
  );

  const mergeDirtyFields = useCallback((fields: DirtyModelFields<Record<string, unknown>>) => {
    setDirtyFields((prev) => ({ ...prev, ...fields }));
  }, []);

  const resetDirtyFields = useCallback(() => {
    setDirtyFields({});
  }, []);

  const draftTimes = useMemo(
    () => ({
      timeStarted: draft.timeStarted,
      timeEnded: draft.timeEnded,
    }),
    [draft.timeStarted, draft.timeEnded],
  );

  return {
    draft,
    setDraft,
    updateDraft,
    dirtyFields,
    setDirtyFields,
    mergeDirtyFields,
    resetDirtyFields,
    draftTimes,
  };
};
